import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Jazzicon, { jsNumberForAddress } from 'react-jazzicon';
import { useAccount, useSignMessage } from 'wagmi';
import { CPGABI, ERC20ABI } from '../../../ABI/ABI';
import Loader from '../../../components/loader/Loader';
import {
    CPGAddress,
    DIRECT_PAYMENT,
    TokenContext,
} from '../../../constant/constant';
import {
    TRANSACTION_ERROR_MSG,
    TRANSACTION_SUCCESS_MSG,
    USER_REJECT_TRANSACTION_MSG,
} from '../../../constant/toasterMessage';
import { copyIcon } from '../../../constant/icon';
import {
    convertInTokenBigAmount,
    copyToClipboard,
    fetchTokenBalances,
    fireErrorToast,
    fireSuccessToast,
    shortenWalletAddress,
} from '../../../utils/commonFunction';
import { ContractWrite, readContractData } from '../../../utils/contractInstance';
import {
    assessPaymentRequestBalance,
    formatInsufficientBalanceMessage,
} from '../../../utils/paymentRequestBalance';
import AmountDisplay from '../../../components/amount/AmountDisplay';
import { persistPaymentRequestResolution } from '../../../api/backendClient';
import { getIncomingPaymentRequestMeta } from '../../../utils/paymentRequestNotes';
import { extractRequestResolutionFromReceipt } from '../../../utils/paymentRequestEvents';
import { publicClient } from '../../../utils/wagmiConfig';

const ReviewRequestModal = ({
    request,
    selectedPaymentType,
    onClose,
    onCompleted,
}) => {
    const [loading, setLoading] = useState(false);
    const [closing, setClosing] = useState(false);
    const dialogRef = useRef(null);
    const { tokenList, setTokenList } = useContext(TokenContext);
    const { address: walletAddress, chainId } = useAccount();
    const { signMessageAsync } = useSignMessage();

    const requestDescription = useMemo(() => {
        if (request?.description?.trim()) return request.description.trim();
        if (!request?.requester || request?.id == null) return '';
        return (
            getIncomingPaymentRequestMeta({
                chainId,
                requester: request.requester,
                requestId: request.id,
                kind: request.kind,
            }).description || ''
        ).trim();
    }, [
        chainId,
        request?.description,
        request?.id,
        request?.kind,
        request?.requester,
    ]);

    const isDirect =
        request?.kind === 'direct' || selectedPaymentType === DIRECT_PAYMENT;

    const balanceCheck = useMemo(() => {
        if (!request?.tokenAddress) return null;
        return assessPaymentRequestBalance({
            tokenList,
            tokenAddress: request.tokenAddress,
            parsedNumber: request.parsedNumber,
            tokenDecimal: request.tokenDecimal,
        });
    }, [
        tokenList,
        request?.tokenAddress,
        request?.parsedNumber,
        request?.tokenDecimal,
    ]);

    const hasInsufficientBalance =
        balanceCheck && balanceCheck.sufficient === false;
    const insufficientBalanceMessage =
        balanceCheck && hasInsufficientBalance
            ? formatInsufficientBalanceMessage(
                  request?.tokenName,
                  balanceCheck.balanceFormatted,
                  balanceCheck.requiredFormatted,
              )
            : '';

    const handleClose = () => {
        if (loading) return;
        setClosing(true);
        // small fade-out delay
        setTimeout(() => onClose?.(), 180);
    };

    const refreshBalances = async () => {
        if (!walletAddress) return;
        try {
            const all = await fetchTokenBalances(walletAddress);
            setTokenList(all);
        } catch (_e) {
            // best-effort
        }
    };

    const persistAcceptResolution = async ({ transactionHash, kind }) => {
        if (!transactionHash || !walletAddress || request?.id == null) return;
        try {
            const receipt = await publicClient.waitForTransactionReceipt({
                hash: transactionHash,
            });
            const resolution = extractRequestResolutionFromReceipt(receipt);
            if (!resolution) return;

            const apiKind = kind === 'milestone' ? 'milestone' : 'direct';
            const result = await persistPaymentRequestResolution(
                walletAddress,
                {
                    kind: apiKind,
                    requestId: resolution.requestId ?? request.id,
                    status: resolution.status || 'accepted',
                    acceptTxHash: transactionHash,
                    linkedPaymentId: resolution.linkedPaymentId,
                },
                { signMessageAsync },
            );

            if (!result.ok) {
                console.warn(
                    'Failed to update payment request on backend:',
                    result.reason,
                );
            }
        } catch (_e) {
            // On-chain accept succeeded; backend mirror is best-effort.
        }
    };

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') handleClose();
        };
        window.addEventListener('keydown', onKey);

        // Prevent the page behind the modal from scrolling/jittering.
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        if (request?.tokenAddress && walletAddress) {
            void refreshBalances();
        }

        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = previousOverflow;
        };
        // eslint-disable-next-line
    }, [loading, request?.id, request?.tokenAddress, walletAddress]);

    const onWriteError = (error) => {
        if (error) {
            const isUserReject = error?.details?.includes(
                'MetaMask Tx Signature: User denied transaction signature',
            );
            fireErrorToast(
                isUserReject ? USER_REJECT_TRANSACTION_MSG : TRANSACTION_ERROR_MSG,
            );
        }
        setLoading(false);
    };

    // -------- Approve allowance --------
    const {
        writeContract: approve,
        writeSuccessResponse: approveResponse,
        writeErrorResponse: approveErrorResponse,
    } = ContractWrite({
        address: request?.tokenAddress,
        abi: ERC20ABI,
        functionName: 'approve',
        initialArgs: [CPGAddress, 0n],
    });

    useEffect(() => {
        if (approveResponse) {
            if (isDirect) {
                acceptPaymentRequest({
                    abi: CPGABI,
                    address: CPGAddress,
                    functionName: 'acceptPaymentRequest',
                    args: [request.id.toString()],
                });
            } else {
                acceptLockedPaymentRequest({
                    abi: CPGABI,
                    address: CPGAddress,
                    functionName: 'acceptLockedPaymentRequest',
                    args: [request.id.toString()],
                });
            }
        }
        // eslint-disable-next-line
    }, [approveResponse]);

    useEffect(() => {
        if (approveErrorResponse) {
            onWriteError(approveErrorResponse);
        }
        // eslint-disable-next-line
    }, [approveErrorResponse]);

    // -------- Accept Direct request --------
    const {
        writeContract: acceptPaymentRequest,
        writeSuccessResponse: acceptDirectResponse,
        writeErrorResponse: acceptDirectError,
        transactionHash: acceptDirectTxHash,
    } = ContractWrite({
        address: CPGAddress,
        abi: CPGABI,
        functionName: 'acceptPaymentRequest',
        initialArgs: [request?.id?.toString() || '0'],
        enabled: true,
    });

    useEffect(() => {
        if (acceptDirectResponse) {
            void persistAcceptResolution({
                transactionHash: acceptDirectTxHash,
                kind: 'direct',
            });
            setLoading(false);
            fireSuccessToast(TRANSACTION_SUCCESS_MSG);
            refreshBalances();
            onCompleted?.();
        }
        // eslint-disable-next-line
    }, [acceptDirectResponse]);

    useEffect(() => {
        if (acceptDirectError) onWriteError(acceptDirectError);
        // eslint-disable-next-line
    }, [acceptDirectError]);

    // -------- Accept Tracked request --------
    const {
        writeContract: acceptLockedPaymentRequest,
        writeSuccessResponse: acceptTrackedResponse,
        writeErrorResponse: acceptTrackedError,
        transactionHash: acceptTrackedTxHash,
    } = ContractWrite({
        address: CPGAddress,
        abi: CPGABI,
        functionName: 'acceptLockedPaymentRequest',
        initialArgs: [request?.id?.toString() || '0'],
        enabled: true,
    });

    useEffect(() => {
        if (acceptTrackedResponse) {
            void persistAcceptResolution({
                transactionHash: acceptTrackedTxHash,
                kind: 'milestone',
            });
            setLoading(false);
            fireSuccessToast(TRANSACTION_SUCCESS_MSG);
            refreshBalances();
            onCompleted?.();
        }
        // eslint-disable-next-line
    }, [acceptTrackedResponse]);

    useEffect(() => {
        if (acceptTrackedError) onWriteError(acceptTrackedError);
        // eslint-disable-next-line
    }, [acceptTrackedError]);

    const getAllowance = async () =>
        readContractData(request.tokenAddress, ERC20ABI, 'allowance', [
            walletAddress,
            CPGAddress,
        ]);

    const handleApprovePay = async () => {
        const latestCheck = assessPaymentRequestBalance({
            tokenList,
            tokenAddress: request.tokenAddress,
            parsedNumber: request.parsedNumber,
            tokenDecimal: request.tokenDecimal,
        });

        if (!latestCheck.sufficient) {
            fireErrorToast(
                formatInsufficientBalanceMessage(
                    request.tokenName,
                    latestCheck.balanceFormatted,
                    latestCheck.requiredFormatted,
                ),
            );
            return;
        }

        try {
            setLoading(true);
            const allowance = await getAllowance();
            const approvalAmount = convertInTokenBigAmount(
                request.parsedNumber,
                request.tokenDecimal,
            );

            if (Number(allowance) < Number(approvalAmount)) {
                approve({
                    abi: ERC20ABI,
                    address: request.tokenAddress,
                    functionName: 'approve',
                    args: [CPGAddress, approvalAmount],
                });
            } else if (isDirect) {
                acceptPaymentRequest({
                    abi: CPGABI,
                    address: CPGAddress,
                    functionName: 'acceptPaymentRequest',
                    args: [request.id.toString()],
                });
            } else {
                acceptLockedPaymentRequest({
                    abi: CPGABI,
                    address: CPGAddress,
                    functionName: 'acceptLockedPaymentRequest',
                    args: [request.id.toString()],
                });
            }
        } catch (error) {
            console.warn('Failed to accept payment request:', error?.message);
            setLoading(false);
        }
    };

    if (!request) return null;
    if (typeof document === 'undefined') return null;

    // Render through a portal because the panel that opens this dialog uses
    // `backdrop-filter` (backdrop-blur), which creates a containing block for
    // any `position: fixed` descendant and would clip the modal otherwise.
    // `isolation: isolate` gives the overlay its own stacking context so no
    // sibling on the page can ever paint above it regardless of z-index.
    return createPortal(
        <div
            className={`fixed inset-0 flex items-center justify-center px-4 transition-opacity duration-200 ${
                closing ? 'opacity-0' : 'opacity-100'
            }`}
            style={{ zIndex: 2147483000, isolation: 'isolate' }}
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) handleClose();
            }}
        >
            {/* Backdrop – fully opaque so nothing behind bleeds through */}
            <div
                className="absolute inset-0 bg-zinc-900/80 backdrop-blur-md dark:bg-zinc-950/90"
                aria-hidden="true"
            />

            {/* Dialog */}
            <div
                ref={dialogRef}
                className={`relative w-full max-w-md transform rounded-3xl border border-zinc-200 bg-white shadow-2xl transition-all duration-200 dark:border-zinc-800 dark:bg-zinc-950 ${
                    closing ? 'scale-95' : 'scale-100'
                }`}
                role="dialog"
                aria-modal="true"
            >
                {/* Header */}
                <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-4 dark:border-zinc-800">
                    <h3 className="text-lg font-bold text-zinc-900 dark:text-white">
                        Review Payment Request
                    </h3>
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={loading}
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900 disabled:opacity-50 dark:hover:bg-zinc-800 dark:hover:text-white"
                        aria-label="Close"
                    >
                        <i className="fa-solid fa-xmark text-sm" />
                    </button>
                </div>

                {/* Body */}
                <div className="space-y-4 px-5 py-5">
                    {/* Counterparty + amount card */}
                    <div className="flex items-center justify-between gap-3 rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-4 dark:border-zinc-800 dark:bg-zinc-900/60">
                        <div className="flex min-w-0 items-center gap-3">
                            <div className="flex-shrink-0 overflow-hidden rounded-full ring-1 ring-zinc-300 dark:ring-zinc-600">
                                <Jazzicon
                                    diameter={40}
                                    seed={jsNumberForAddress(request.requester || '')}
                                />
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-semibold text-zinc-900 dark:text-white">Wallet</p>
                                <div className="mt-0.5 flex min-w-0 items-center gap-1.5">
                                    <span className="truncate font-mono text-[11px] text-zinc-500 dark:text-zinc-400">
                                        {shortenWalletAddress(request.requester)}
                                    </span>
                                    {request.requester && (
                                        <button
                                            type="button"
                                            onClick={() =>
                                                copyToClipboard(request.requester)
                                            }
                                            className="text-zinc-500 transition-colors hover:text-teal-600 dark:hover:text-teal-400"
                                            title="Copy"
                                        >
                                            <i className={`${copyIcon} text-[10px]`} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex-shrink-0 text-right">
                            <AmountDisplay
                                value={request.parsedNumber ?? request.amountFormatted}
                                className="text-base font-bold text-zinc-900 dark:text-white"
                                suffix={request.tokenName}
                                suffixClassName="text-xs font-medium text-zinc-500 dark:text-zinc-400"
                            />
                            {balanceCheck ? (
                                <p
                                    className={`mt-0.5 text-[10px] ${
                                        hasInsufficientBalance
                                            ? 'font-medium text-rose-600 dark:text-rose-400'
                                            : 'text-zinc-500'
                                    }`}
                                >
                                    Your balance: {balanceCheck.balanceFormatted}{' '}
                                    {request.tokenName}
                                </p>
                            ) : null}
                        </div>
                    </div>

                    {/* Meta grid */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-3.5 py-3 dark:border-zinc-800 dark:bg-zinc-900/50">
                            <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                                Payment Type
                            </p>
                            {isDirect ? (
                                <p className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-emerald-700 dark:text-emerald-300">
                                    <i className="fa-solid fa-arrow-up-long" />
                                    Direct Transfer
                                </p>
                            ) : (
                                <p className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-teal-700 dark:text-teal-300">
                                    <i className="fa-regular fa-clock" />
                                    Tracked Escrow
                                </p>
                            )}
                        </div>
                        <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-3.5 py-3 dark:border-zinc-800 dark:bg-zinc-900/50">
                            <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                                Request ID
                            </p>
                            <p className="font-mono text-[13px] font-semibold text-zinc-800 dark:text-zinc-200">
                                #{request.id}
                            </p>
                        </div>
                    </div>

                    {!isDirect && request.milestoneCount ? (
                        <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-3.5 py-3 dark:border-zinc-800 dark:bg-zinc-900/50">
                            <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                                Milestones
                            </p>
                            <p className="text-[13px] font-semibold text-zinc-800 dark:text-zinc-200">
                                {request.milestoneCount} ×{' '}
                                <span className="font-normal text-zinc-500 dark:text-zinc-400">
                                    {request.milestoneIntervalLabel || '—'}
                                </span>
                            </p>
                        </div>
                    ) : null}

                    {requestDescription ? (
                        <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-3.5 py-3 dark:border-zinc-800 dark:bg-zinc-900/50">
                            <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                                Description
                            </p>
                            <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-zinc-800 dark:text-zinc-200">
                                {requestDescription}
                            </p>
                        </div>
                    ) : null}

                    {hasInsufficientBalance ? (
                        <div className="flex items-start gap-2.5 rounded-xl border border-rose-500/30 bg-rose-500/[0.08] px-3.5 py-3 dark:bg-rose-500/[0.06]">
                            <i className="fa-solid fa-triangle-exclamation mt-0.5 text-sm text-rose-600 dark:text-rose-400" />
                            <p className="text-[12px] leading-relaxed text-rose-900 dark:text-rose-100">
                                {insufficientBalanceMessage} Add funds to your wallet
                                before approving this request.
                            </p>
                        </div>
                    ) : (
                        <div className="flex items-start gap-2.5 rounded-xl border border-amber-500/25 bg-amber-500/[0.06] px-3.5 py-3 dark:bg-amber-500/[0.05]">
                            <i className="fa-solid fa-circle-info mt-0.5 text-sm text-amber-600 dark:text-amber-400" />
                            <p className="text-[12px] leading-relaxed text-zinc-700 dark:text-zinc-300">
                                Approving this request will immediately initiate a{' '}
                                <AmountDisplay
                                    value={request.parsedNumber ?? request.amountFormatted}
                                    className="font-semibold text-zinc-900 dark:text-white"
                                    suffix={request.tokenName}
                                    suffixClassName="font-semibold text-zinc-900 dark:text-white"
                                />{' '}
                                transfer from your wallet. This action cannot be undone.
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="grid grid-cols-2 gap-3 px-5 pb-5">
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={loading}
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-rose-300 px-4 py-2.5 text-[13px] font-semibold text-rose-700 transition-colors hover:bg-rose-50 disabled:opacity-50 dark:border-rose-500/40 dark:text-rose-300 dark:hover:bg-rose-500/10"
                    >
                        <i className="fa-regular fa-circle-xmark" />
                        Decline
                    </button>
                    {loading ? (
                        <div className="inline-flex items-center justify-center rounded-xl bg-teal-100 px-4 py-2.5 dark:bg-emerald-900/50">
                            <Loader size="20px" stroke="#0d9488" />
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={handleApprovePay}
                            disabled={hasInsufficientBalance}
                            title={
                                hasInsufficientBalance
                                    ? insufficientBalanceMessage
                                    : undefined
                            }
                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-teal-600 px-4 py-2.5 text-[13px] font-semibold text-white shadow-md transition-all hover:bg-teal-500 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gradient-to-r dark:from-emerald-600 dark:to-teal-600 dark:shadow-emerald-900/30 dark:hover:from-emerald-500 dark:hover:to-teal-500"
                        >
                            <i className="fa-regular fa-circle-check" />
                            Approve &amp; Pay
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default ReviewRequestModal;
